import styled from '@emotion/styled';

import type { Site } from '@hotjar/site';
import type { Organization } from '@hotjar/organization';
import { tokens, Loader, Divider } from '@hotjar/hotsauce';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useSites } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { LogRequestSection } from './LogRequestSection';
import { RequestByAppSection } from './RequestByAppSection';
import { RequestByApiSection } from './RequestByApiSection';

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

interface RequestSectionProps {
  organization: Organization;
}

export const RequestSection = ({ organization }: RequestSectionProps): JSX.Element => {
  const { query: sitesQuery } = useSites({
    selector: (userResources) => {
      return userResources.sites.filter((site) => site.organizationId === organization.id);
    },
  });
  const { data: sites, isLoading: sitesQueryIsLoading } = sitesQuery;
  const { data: currentUserData, isLoading: currentUserIsLoading } = useCurrentUser();
  const { hasPermission, isPermissionFetching } = useAuthorization();
  const hasLookupCreatePermission = hasPermission(
    'organization',
    `${organization.id}`,
    'visitor-lookups-create',
  );
  const hasLookupReadPermission = hasPermission(
    'organization',
    `${organization.id}`,
    'visitor-lookups-read',
  );
  const isUserLookupApiEnabled = useFeatureFlag('user_lookup_public_api');

  return (
    <Loader isLoading={isPermissionFetching || sitesQueryIsLoading || currentUserIsLoading}>
      <SectionWrapper>
        <RequestByAppSection
          organization={organization}
          hasLookupCreatePermission={hasLookupCreatePermission}
          currentUserDataEmail={currentUserData?.email as string}
          sites={sites as Site[]}
        />
        {isUserLookupApiEnabled && (
          <>
            <Divider />
            <RequestByApiSection
              organization={organization}
              hasLookupCreatePermission={hasLookupCreatePermission}
              hasLookupReadPermission={hasLookupReadPermission}
            />
          </>
        )}
        <Divider />
        <LogRequestSection organization={organization} />
      </SectionWrapper>
    </Loader>
  );
};
